"use client";
import { Link } from "@/i18n/routing";
import {
  IconForwardArrow,
  IconMenuLinkArrow,
  IconSmallForwardArrow,
} from "@repo/assets/images";
import { HamburgerButton, OffCanvasMenu } from "@repo/ui";
import cx from "classnames";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";

const CategoryMenuStrapi = ({ mainMenu }) => {
  const t = useTranslations("Common");
  const tAccount = useTranslations("Account");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [visibleDrawers, setVisibleDrawers] = useState([true]);
  const [activeMenus, setActiveMenus] = useState([mainMenu]);
  const [menuPath, setMenuPath] = useState([
    {
      title: t("Categories"),
      url: "",
      isRoot: true,
    },
  ]);

  const subMenuLevels = ["Level1", "Level2", "Level3"];

  // Open/close the menu drawer from the hamburger/shop by button
  const handleOpenMenuDrawer = useCallback(() => {
    if (openDrawer) {
      setOpenDrawer(false);
      setTimeout(() => {
        setActiveMenus([mainMenu]);
        setMenuPath([{ title: t("Categories"), url: "/", isRoot: true }]);
        setVisibleDrawers([true]);
      }, 300);
    } else {
      setOpenDrawer(true);
    }
  }, [openDrawer, mainMenu, t]);

  // Close the menu drawer from the back button, cross button and outside click
  const handleClose = useCallback(
    (index) => {
      if (typeof index === "number") {
        // Handle back button click
        // mark the current drawer as not visible (triggers slide-out animation)
        setVisibleDrawers((prev) => {
          const newVisibleDrawers = [...prev];
          newVisibleDrawers[index] = false;
          return newVisibleDrawers;
        });
        // After animation completes (300ms), clean up the state
        setTimeout(() => {
          setActiveMenus((prev) => prev.slice(0, index));
          setMenuPath((prev) => prev.slice(0, index));
          setVisibleDrawers((prev) => prev.slice(0, index)); // Remove drawer visibility flags after current index
        }, 300);
      } else {
        // Handle main close
        setOpenDrawer(false);
        setTimeout(() => {
          setActiveMenus([mainMenu]);
          setMenuPath([{ title: t("Categories"), url: "", isRoot: true }]);
          setVisibleDrawers([true]);
        }, 300);
      }
    },
    [mainMenu, t]
  );

  const handleMenuClick = (item, currentIndex) => {
    const target = item?.attributes || item;
    const nextLevel = getNextLevel(item);

    const isInCurrentChain = menuPath.some(
      (pathItem, index) =>
        index > currentIndex && pathItem.title === target.title
    );

    if (isInCurrentChain) return;

    let updatedMenus = activeMenus.slice(0, currentIndex + 1);
    let updatedPath = menuPath.slice(0, currentIndex + 1);
    let updatedVisibleDrawers = visibleDrawers.slice(0, currentIndex + 1);

    if (nextLevel) {
      // Add the next level menu and update the path
      updatedMenus = [...updatedMenus, target[nextLevel]];
      updatedPath = [
        ...updatedPath,
        {
          title: target.title,
          url: target.url,
        },
      ];

      const isDrawerAlreadyOpen = visibleDrawers[currentIndex + 1];

      if (!isDrawerAlreadyOpen) {
        // Open the next level drawer with animation
        updatedVisibleDrawers = [...updatedVisibleDrawers, false];
        setActiveMenus(updatedMenus);
        setMenuPath(updatedPath);
        setVisibleDrawers(updatedVisibleDrawers);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setVisibleDrawers((prev) => {
              const newVisibleDrawers = [...prev];
              newVisibleDrawers[currentIndex + 1] = true;
              return newVisibleDrawers;
            });
          });
        });
      } else {
        // Next level drawer is already open, just replace the content
        updatedVisibleDrawers = [...updatedVisibleDrawers, true];
        setActiveMenus(updatedMenus);
        setMenuPath(updatedPath);
        setVisibleDrawers(updatedVisibleDrawers);
      }
    } else {
      // If no next level, close any deeper levels
      setActiveMenus(updatedMenus);
      setMenuPath(updatedPath);
      setVisibleDrawers(updatedVisibleDrawers);
    }
  };

  const getNextLevel = (item) => {
    const nextLevel = subMenuLevels.find((level) => {
      const target = item?.attributes || item;
      return (
        target?.[level] &&
        Array.isArray(target[level]) &&
        target[level].length > 0
      );
    });
    return nextLevel;
  };

  const isItemActive = (item, currentIndex) => {
    const target = item?.attributes || item;
    return menuPath[currentIndex + 1]?.title === target.title;
  };

  return (
    <>
      <button
        onClick={handleOpenMenuDrawer}
        className="hidden items-center gap-3 md:flex"
      >
        <span>{t("Shop_By")}</span>
        <IconSmallForwardArrow className="text-white rtl:-rotate-180" />
      </button>

      <HamburgerButton
        open={openDrawer}
        handleOpenMenu={handleOpenMenuDrawer}
        showOnMd={false}
      />

      <div className="flex">
        {activeMenus?.map((menu, index) => {
          const isCategories = menuPath[index]?.isRoot === true;
          const DynamicTag = isCategories ? "div" : Link;
          return (
            <OffCanvasMenu
              key={index}
              open={openDrawer && visibleDrawers[index]}
              handleClose={(idx) => handleClose(idx ?? undefined)}
              title={menuPath[index]?.title}
              position="left"
              index={index}
              totalMenus={activeMenus.length}
            >
              <div className="border-supportiveDarkGray flex flex-col">
                <DynamicTag
                  {...(!isCategories && {
                    href: `/collections${menuPath[index]?.url}`,
                  })}
                  className={cx(
                    "border-supportiveDarkGray font-heading text-supportiveBlack rtl:font-arabic group h-full border-b text-base font-semibold md:text-xl",
                    {
                      "px-4 py-2.5 hover:bg-[#F5F2EE]": isCategories,
                      "hover:text-lightYellow px-4 py-[19.5px]": !isCategories,
                    }
                  )}
                  {...(!isCategories && { onClick: handleOpenMenuDrawer })}
                >
                  <div className="flex items-center justify-between">
                    <span>{menuPath[index]?.title}</span>
                    {/* TODO: Add/Remove this icon when the design is final */}
                    {/* {!isCategories && (
                      <IconMenuLinkArrow className="text-lightYellow rotate-[-45deg] rtl:rotate-[-135deg] transition-transform ltr:group-hover:translate-x-1 rtl:group-hover:-translate-x-1 group-hover:-translate-y-1" />
                    )} */}
                    {!isCategories && (
                      <span className="font-body text-lightYellow rtl:font-arabic text-sm font-semibold">
                        {tAccount("See_All")}
                      </span>
                    )}
                  </div>
                </DynamicTag>
                <ul>
                  {menu?.map((item) => {
                    const target = item?.attributes || item;
                    const hasNextLevel = getNextLevel(item);
                    const isActive = isItemActive(item, index);
                    const DynamicTag = hasNextLevel ? "div" : Link;
                    const isOnSale = index === 0 && target?.isOnSale;
                    const saleTextColor = `text-[${target?.saleColor || "#C62828"}]`;
                    return (
                      <li
                        key={`${item.title}-${item.id}-${index}`}
                        className={cx(
                          "border-supportiveDarkGray text-supportiveBlack group border-b px-4 text-sm font-medium md:text-lg",
                          {
                            "cursor-pointer": hasNextLevel,
                            "hover:text-lightYellow": !hasNextLevel,
                            "bg-lightYellow": isActive,
                          }
                        )}
                      >
                        <DynamicTag
                          {...(!hasNextLevel && {
                            href: `/collections${target?.url}`,
                          })}
                          className="block py-2.5"
                          onClick={
                            hasNextLevel
                              ? () => handleMenuClick(item, index)
                              : handleOpenMenuDrawer
                          }
                        >
                          <div className="flex items-center justify-between">
                            <span
                              className={cx("", {
                                [saleTextColor]: isOnSale,
                              })}
                            >
                              {target?.title}
                            </span>
                            {hasNextLevel ? (
                              <IconForwardArrow className="text-supportiveBlack transition-transform group-hover:ltr:translate-x-1 rtl:rotate-180 group-hover:rtl:-translate-x-1" />
                            ) : (
                              <IconMenuLinkArrow className="text-lightYellow me-2 -rotate-45 transition-transform group-hover:-translate-y-1 ltr:group-hover:translate-x-1 rtl:rotate-[-135deg] rtl:group-hover:-translate-x-1" />
                            )}
                          </div>
                        </DynamicTag>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </OffCanvasMenu>
          );
        })}
      </div>
    </>
  );
};

export default CategoryMenuStrapi;
